/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef } from 'react'
import {
  Card,
  CardContent,
  TextField,
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Avatar,
  Alert
} from '@mui/material'
import CameraAltIcon from '@mui/icons-material/CameraAlt'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { useAuth } from '../../services/authContext'
import {
  RegisterApi,
  ForgetPassscodeApi,
  GetUserApi,
  UpdateUserApi,
  FetchDataFromOtpApi
} from '../../services/apis'
import Grid from '@mui/material/Grid'
import { Select, MenuItem } from '@mui/material'
import { BEBaseRoot } from '../../constants/app.constant'
import MessageModal from '../MessageModal/MessageModal'
import { resizeProfileImage } from '../../utils/imageUtils'

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#e4b648' }
    // Add more theme customizations as needed
  }
})

const SignupForm = ({ handleUpdateSuccess, userType }) => {
  const { loginStatus } = useAuth()
  const isUpdate = !!loginStatus?.token

  // State variables for form fields
  const [phoneNumber, setPhoneNumber] = React.useState('')
  const [firstName, setFirstName] = React.useState('')
  const [lastName, setLastName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [address, setAddress] = React.useState('')
  const [dob, setDob] = React.useState('')
  const [gender, setGender] = React.useState('')
  const [otp, setOtp] = React.useState('')
  const [schoolName, setSchoolName] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [currentPasscode, setCurrentPasscode] = React.useState('')
  const [newPasscode, setNewPasscode] = React.useState('')
  const [confirmPasscode, setConfirmPasscode] = React.useState('')
  const [errors, setErrors] = React.useState(false)
  const [profilePicture, setProfilePicture] = React.useState(null)
  const [profilePictureFile, setProfilePictureFile] = React.useState(null)
  const debounceTimeoutRef = useRef(null)
  const [modalOpen, setModalOpen] = React.useState(false)
  const [modalTitle, setModalTitle] = React.useState('')
  const [modalMessage, setModalMessage] = React.useState('')
  const [signUpFlag, setSignupFlag] = React.useState(false)

  const showModal = (title, message) => {
    setModalTitle(title)
    setModalMessage(message)
    setModalOpen(true)
  }

  const handleFileChange = async event => {
    const file = event.target.files[0]
    if (file) {
      try {
        // Resize the image before setting it in state
        const resizedFile = await resizeProfileImage(file)
        setProfilePictureFile(resizedFile)

        const reader = new window.FileReader()
        reader.onload = () => {
          setProfilePicture(reader.result) // Display the preview
        }
        reader.readAsDataURL(resizedFile)
      } catch (error) {
        console.error("Error resizing image:", error)
        // Fallback to original file if resize fails
        setProfilePictureFile(file)
        const reader = new window.FileReader()
        reader.onload = () => {
          setProfilePicture(reader.result)
        }
        reader.readAsDataURL(file)
      }
    }
  }

  // Add new function to remove profile picture
  const handleRemoveProfilePicture = () => {
    setProfilePicture(null)
    setProfilePictureFile(null)
  }

  React.useEffect(() => {
    if (isUpdate) {
      GetUserApi(loginStatus.user.id)
        .then(response => {
          setPhoneNumber(response.user.phone)
          setFirstName(response.user.first_name)
          setLastName(response.user.last_name)
          setEmail(response.user.email)
          setAddress(response.user.address)
          setDob(response.user.dob)
          setGender(response.user.gender)
          setSchoolName(response.user.school_name)
          setProfilePicture(response.user.profile_picture)
        })
        .catch(error => {
          console.log(error)
        })
    }
  }, [isUpdate])

  // Debounce function

  // Debounced API call instance
  const validateField = async (field, value) => {
    let error = "";

    const debouncedFetchData = (value, delay) => {
      return new Promise((resolve) => {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(async () => {
          try {
            const response = await FetchDataFromOtpApi({
              otp: value,
              id: loginStatus.user.id
            });

            resolve(response);
          } catch (error) {
            console.error("Error fetching OTP data:", error);
            resolve({ detail: "An error occurred while verifying the passcode." });
          }
        }, delay);
      });
    };

    switch (field) {
      case "currentPasscode": {
        const response = await debouncedFetchData(value, 500);

        if (response.detail === "Current passcode is incorrect.") {
          error = "Current passcode is incorrect.";
        }
        break;
      }

      case "newPasscode":
        if (!currentPasscode) error = "Current passcode is required.";
        if (!value) error = "New passcode is required.";
        else if (!/^[0-9]{6,6}$/.test(value)) error = "Passcode should be 6 digits.";

        // ✅ Check if confirmPasscode already matches
        if (value && value === confirmPasscode) {
          setOtp(value);
        }
        break;

      case "confirmPasscode":
        if (!value) error = "Confirm passcode is required.";
        else if (value !== newPasscode) error = "Passcode should match with new passcode.";
        else if (newPasscode && value === newPasscode) {
          // ✅ Set OTP when confirmPasscode matches newPasscode
          setOtp(newPasscode);
        }
        break;

      default:
        break;
    }

    setErrors((prevErrors) => ({ ...prevErrors, [field]: error }));
  };

  const errorData = (msg) => {
    return (
      <>
        {Object.entries(msg).map(([field, messages], index) => (
          <Alert key={index} severity="error" sx={{ mb: 1 }}>
            <strong>{field}:</strong> {messages.join(', ')}
          </Alert>
        ))}
      </>
    );
  };


  React.useEffect(() => {
    if (newPasscode && confirmPasscode && newPasscode === confirmPasscode) {
      if (newPasscode.length === 6) {
        setOtp(newPasscode);
      }
    } else {
      setOtp(undefined); // or setOtp('') based on your initial state
    }
  }, [newPasscode, confirmPasscode]);

  const handleSignup = async () => {
    const formData = new window.FormData()
    const userName = Math.floor(1000000000 + Math.random() * 9000000000);
    formData.append('phone', phoneNumber)
    formData.append('username', userName)
    formData.append('first_name', firstName)
    formData.append('last_name', lastName)
    formData.append('school_name', schoolName)
    formData.append('email', email)
    formData.append('address', address)
    formData.append('date_joined', new Date().toISOString())
    formData.append('dob', dob)
    formData.append('gender', gender)
    formData.append('status', 'active')
    formData.append('staff_type', userType === "company" ? "user" : "user")
    formData.append(
      'password',
      firstName.toUpperCase() + lastName.toLowerCase() + Math.floor(Math.random() * 999999)
    )
    if (profilePictureFile) {
      formData.append('profile_picture', profilePictureFile)
    } else if (profilePicture === null && isUpdate) {
      formData.append('remove_profile_picture', 'true')
    }

    try {
      if (isUpdate) {
        formData.delete('staff_type')
        formData.delete('username')
        if (otp) {
          formData.append('otp', otp)
          formData.append('current_passcode', currentPasscode)
        }

        await UpdateUserApi(formData, loginStatus.user.id)
          .then(response => {
            console.log(response)
            showModal('Success', 'User updated successfully')
            // Call handleUpdateSuccess after a 3-second delay
            setTimeout(() => {
              handleUpdateSuccess();
            }, 2000); // 3000 milliseconds = 3 seconds
            window.location.reload();
          })
          .catch(error => {
            console.log(error)
            showModal('Error', error.message || 'Some error occurred')
          })
      } else {
        setLoading(true)
        await RegisterApi(formData)
        await ForgetPassscodeApi({ phone: userName })
        setSignupFlag(true)
        showModal('Success', 'User registered successfully')
      }
    } catch (error) {
      console.error(error)
      showModal('Error', errorData(JSON.parse(error.message)))


    } finally {
      setLoading(false)
    }
  }

  const SignupCard = (
    <React.Fragment>
      <Card
        sx={{
          backgroundColor: 'white',
          boxShadow: 3,
          borderRadius: 2,
          padding: 2
        }}
      >
        <CardContent
          sx={{
            maxHeight: 'calc(100vh - 200px)', // Adjust the height depending on your layout
            overflowY: 'auto', // Enable vertical scrolling
            overflowX: 'hidden', // Hide horizontal overflow
            paddingBottom: '16px',
            backgroundColor: 'white' // Ensure there's some padding at the bottom
          }}
        >
          <Typography
            variant='h5'
            component='h4'
            sx={{
              color: theme.palette.primary.main,
              textAlign: 'center',
              marginBottom: 3,
              fontWeight: 'bold'
            }}
          >
            {isUpdate ? 'Update User' : 'Sign Up to Join the Platform'}
          </Typography>

          <Grid container rowSpacing={2} columnSpacing={3}>
            {' '}
            {/* Responsive column spacing */}
            {/* Profile Picture */}
            <Grid
              item
              xs={12}
              sm={12}
              sx={{ textAlign: 'center', marginBottom: 2 }}
            >
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Avatar
                  src={
                    profilePicture?.startsWith('data:image') // Check if it's a base64 string
                      ? profilePicture // Use as-is
                      : profilePicture
                        ? BEBaseRoot.slice(0, -1) + profilePicture // Use base URL for media path
                        : '/profile.jpg' // Fallback image
                  }
                  alt="Profile Picture"
                  sx={{
                    width: 120,
                    height: 120,
                    margin: '0 auto',
                    border: '2px solid #e4b648'
                  }}
                />
                <IconButton
                  component='label'
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: '20%',
                    backgroundColor: '#e4b648',
                    color: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#d4a238'
                    }
                  }}
                >
                  <CameraAltIcon />
                  <input
                    type='file'
                    hidden
                    accept='image/*'
                    onChange={handleFileChange}
                  />
                </IconButton>
                {/* Add Remove Profile Picture Button */}
                {profilePicture && (
                  <IconButton
                    onClick={handleRemoveProfilePicture}
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: '20%',
                      backgroundColor: '#e74c3c',
                      color: '#ffffff',
                      '&:hover': {
                        backgroundColor: '#c0392b'
                      }
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 6h18"></path>
                      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                  </IconButton>
                )}
              </Box>
            </Grid>
            {/* Phone Number */}
            {!isUpdate && (
              <Grid item xs={12} sm={6}>
                <label
                  style={{
                    marginBottom: '8px',
                    fontWeight: 'bold',
                    color: 'black'
                  }}
                >
                  Phone Number
                </label>
                <TextField
                  variant='outlined'
                  size='small'
                  placeholder='Phone Number'
                  fullWidth
                  value={phoneNumber}
                  onChange={e => setPhoneNumber(e.target.value)}
                />
              </Grid>
            )}

            {/* First Name */}
            <Grid item xs={12} sm={6}>
              <label
                style={{
                  marginBottom: '8px',
                  fontWeight: 'bold',
                  color: 'black'
                }}
              >
                First Name
              </label>
              <TextField
                variant='outlined'
                size='small'
                placeholder='First Name'
                fullWidth
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
              />
            </Grid>
            {/* Last Name */}
            <Grid item xs={12} sm={6}>
              <label
                style={{
                  marginBottom: '8px',
                  fontWeight: 'bold',
                  color: 'black'
                }}
              >
                Last Name
              </label>
              <TextField
                variant='outlined'
                size='small'
                placeholder='Last Name'
                fullWidth
                value={lastName}
                onChange={e => setLastName(e.target.value)}
              />
            </Grid>
            {/* Email Address */}
            <Grid item xs={12} sm={6}>
              <label
                style={{
                  marginBottom: '8px',
                  fontWeight: 'bold',
                  color: 'black'
                }}
              >
                Email Address
              </label>
              <TextField
                variant='outlined'
                size='small'
                type='email'
                placeholder='Email Address'
                fullWidth
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </Grid>


            {false && (
              <>
                {/* Address */}
                <Grid item xs={12} sm={6}>
                  <label
                    style={{
                      marginBottom: '8px',
                      fontWeight: 'bold',
                      color: 'black'
                    }}
                  >
                    Address
                  </label>
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='Address'
                    fullWidth
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                  />
                </Grid>
                {/* Date of Birth */}
                <Grid item xs={12} sm={6}>
                  <label
                    style={{
                      marginBottom: '8px',
                      fontWeight: 'bold',
                      color: 'black'
                    }}
                  >
                    Date of Birth
                  </label>
                  <TextField
                    variant='outlined'
                    size='small'
                    type='date'
                    fullWidth
                    value={dob}
                    onChange={e => setDob(e.target.value)}
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                </Grid>
                {/* Gender */}
                <Grid item xs={12} sm={6}>
                  <label
                    style={{
                      marginBottom: '8px',
                      fontWeight: 'bold',
                      color: 'black'
                    }}
                  >
                    Gender
                  </label>
                  <Select
                    value={gender}
                    onChange={e => setGender(e.target.value)}
                    fullWidth
                    size='small'
                    sx={{ backgroundColor: 'white' }}
                  >
                    {/* Placeholder as default option */}
                    <MenuItem value='' disabled>
                      Select Gender
                    </MenuItem>

                    {/* Gender Options */}
                    <MenuItem style={{ color: 'black' }} value='male'>
                      Male
                    </MenuItem>
                    <MenuItem style={{ color: 'black' }} value='female'>
                      Female
                    </MenuItem>
                    <MenuItem style={{ color: 'black' }} value='others'>
                      Others
                    </MenuItem>
                  </Select>
                </Grid>

              </>)}
            <Grid item xs={12} sm={6}>

              {/* {false && loginStatus.user?.staff_type === "client" || loginStatus.user?.staff_type === "student" ?
                <></>
                :
                <>

                  <label
                    style={{
                      marginBottom: '8px',
                      fontWeight: 'bold',
                      color: 'black'
                    }}
                  >
                    Company Name
                  </label>
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='Company Name'
                    style={{
                      color: 'black', // For regular text color
                      backgroundColor: 'white' // Ensure contrast
                    }}
                    fullWidth
                    value={schoolName}
                    onChange={e => setSchoolName(e.target.value)}
                  />
                </>
              } */}


            </Grid>
            {isUpdate && (
              <Box
                sx={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: '#ffffff'
                }}
              >
                <hr />
                <Typography
                  variant='h6'
                  sx={{
                    fontWeight: 'bold',
                    color: '#e4b648',
                    marginBottom: '16px',
                    textAlign: 'center' // Center-align the header text
                  }}
                >
                  Passcode Change
                </Typography>
                {/* Current Passcode */}
                <Box sx={{ marginBottom: '16px' }}>
                  <label
                    style={{
                      marginBottom: '8px',
                      fontWeight: 'bold',
                      color: 'black',
                      display: 'block'
                    }}
                  >
                    Current Passcode
                  </label>
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='Enter your current passcode'
                    fullWidth
                    value={currentPasscode}
                    onChange={e => {
                      setCurrentPasscode(e.target.value)
                      validateField('currentPasscode', e.target.value)
                    }}
                    error={!!errors.currentPasscode}
                    helperText={errors.currentPasscode}
                  />
                </Box>

                {/* New Passcode */}
                <Box sx={{ marginBottom: '16px' }}>
                  <label
                    style={{
                      marginBottom: '8px',
                      fontWeight: 'bold',
                      color: 'black',
                      display: 'block'
                    }}
                  >
                    New Passcode
                  </label>
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='Enter your new passcode'
                    fullWidth
                    value={newPasscode}
                    onChange={e => {
                      setNewPasscode(e.target.value)
                      validateField('newPasscode', e.target.value)
                    }}
                    error={!!errors.newPasscode}
                    helperText={errors.newPasscode}
                  />
                </Box>

                {/* Confirm Passcode */}
                <Box>
                  <label
                    style={{
                      marginBottom: '8px',
                      fontWeight: 'bold',
                      color: 'black',
                      display: 'block'
                    }}
                  >
                    Confirm New Passcode
                  </label>
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='Confirm your new passcode'
                    fullWidth
                    value={confirmPasscode}
                    onChange={e => setConfirmPasscode(e.target.value)}
                    error={!!confirmPasscode && confirmPasscode !== newPasscode}
                    helperText={
                      !!confirmPasscode && confirmPasscode !== newPasscode
                        ? 'Confirm Passcode should match with New Passcode.'
                        : ''
                    }
                  />
                </Box>
              </Box>
            )}

          </Grid>
          {/* Signup Form Submit Button */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button
              variant='contained'
              color='primary'
              onClick={handleSignup}
              disabled={loading}
              sx={{
                color: 'black', // Set the text color to black
                bgcolor: '#eda811', // Set the background color
                '&:hover': {
                  bgcolor: 'grey' // Set the background color to grey on hover
                }
              }}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : isUpdate ? (
                'Update'
              ) : (
                'Sign Up'
              )}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </React.Fragment>
  )

  return (
    <ThemeProvider theme={theme}>
      <div className='SignupForm' data-testid='SignupForm'>
        <Box
          sx={{
            minWidth: 275,
            display: 'flex',
            justifyContent: 'center',
            width: '100%'
          }}
          className='perfect-center'
        >
          {signUpFlag ?
            <Card variant='outlined'>User registered successfully. Passcode is sent to your Phone</Card>
            :
            <Card variant='outlined'>{SignupCard}</Card>
          }
          <MessageModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            title={modalTitle}
            message={modalMessage}
          />
        </Box>
      </div>
    </ThemeProvider>
  )
}

SignupForm.propTypes = {}

export default SignupForm
